import { Button, useNotify } from "react-admin";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { useField } from "react-final-form";
import { cloneDeep, find, findIndex } from "lodash";
import SanitizedGrid from "../../../components/SanitizedGrid";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  removeSchoolLocationFromRouteLocation,
  reOptimiseRouteLocation,
} from "../tools";
import AiLoading from "./AiLoading";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  autocompleteInput: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

interface MoveRouteLocationFromMarkerButtonProps {
  routeName: string;
  order: number;
  setIsPopupVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const MoveRouteLocationFromMarkerButton: React.FC<
  MoveRouteLocationFromMarkerButtonProps
> = ({ routeName, order, setIsPopupVisible }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [oldRouteLocation, setOldRouteLocation] = useState<any>(null);
  const [targetRoute, setTargetRoute] = useState<any>(null);

  const formRoutesChangedField = useField("formRoutesChanged");
  const routesField = useField("routes");
  const busScheduleField = useField("busSchedule");
  const schoolField = useField("school");
  const maxStopTimeField = useField("maxStopTime");

  const classes = useStyles();
  const notify = useNotify();

  const handleOpenDialog = () => {
    const foundRoute = find(routesField.input.value, ["routeName", routeName]);

    if (!foundRoute) {
      notify(`Route "${routeName}" not found`, "error");
      return;
    }

    const foundLocation = find(foundRoute.routeLocations, ["order", order]);

    if (!foundLocation) {
      notify(
        `Location with order ${order} not found in route "${routeName}"`,
        "error",
      );
      return;
    }

    setOldRouteLocation(foundLocation);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMoveRouteLocation = async () => {
    try {
      setIsLoading(true);

      const routes = cloneDeep(routesField.input.value);

      const oldRouteIndex = findIndex(routes, ["routeName", routeName]);
      if (oldRouteIndex === -1) {
        notify("Route not found", "error");
        setIsLoading(false);
        return;
      }

      const oldLocationIndex = findIndex(routes[oldRouteIndex].routeLocations, [
        "order",
        order,
      ]);
      if (oldLocationIndex === -1) {
        notify("Old route location not found", "error");
        setIsLoading(false);
        return;
      }

      const newRouteIndex = findIndex(routes, [
        "routeName",
        targetRoute.routeName,
      ]);
      if (newRouteIndex === -1) {
        notify("Target route not found", "error");
        setIsLoading(false);
        return;
      }

      if (oldRouteIndex === newRouteIndex) {
        notify("Location is already in this route", "warning");
        setIsLoading(false);
        return;
      }

      const clonedLocationToInsert = {
        ...oldRouteLocation,
        id: 0, // reset as intended
      };

      const oldRoute = routes[oldRouteIndex];
      oldRoute.routeGeometries = [];

      const oldRouteLocationsAfterRemoved = [
        ...oldRoute.routeLocations.slice(0, oldLocationIndex),
        ...oldRoute.routeLocations.slice(oldLocationIndex + 1),
      ];

      const oldRouteWithoutSchool = removeSchoolLocationFromRouteLocation(
        oldRouteLocationsAfterRemoved,
        busScheduleField.input.value.type,
      );

      const optimizedOldRouteLocations = await reOptimiseRouteLocation(
        schoolField.input.value,
        busScheduleField.input.value,
        maxStopTimeField.input.value,
        oldRouteWithoutSchool,
      );

      oldRoute.routeLocations = optimizedOldRouteLocations;

      const newRoute = routes[newRouteIndex];
      newRoute.routeGeometries = [];

      const insertIndex = newRoute.routeLocations.length - 1;
      const newRouteLocations = [
        ...newRoute.routeLocations.slice(0, insertIndex),
        clonedLocationToInsert,
        ...newRoute.routeLocations.slice(insertIndex),
      ];

      const newRouteWithoutSchool = removeSchoolLocationFromRouteLocation(
        newRouteLocations,
        busScheduleField.input.value.type,
      );

      const optimizedNewRouteLocations = await reOptimiseRouteLocation(
        schoolField.input.value,
        busScheduleField.input.value,
        maxStopTimeField.input.value,
        newRouteWithoutSchool,
      );

      newRoute.routeLocations = optimizedNewRouteLocations;

      routesField.input.onChange(routes);
      formRoutesChangedField.input.onChange(true);

      setIsLoading(false);
      setIsPopupVisible(false);
      handleClose();
    } catch (error) {
      notify("Failed to move location between routes", "error");
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        label="Move to Another Route"
        onClick={handleOpenDialog}
        style={{ marginLeft: 0 }}
      />

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="sm"
      >
        <DialogTitle id="alert-dialog-title">
          Move Location {oldRouteLocation?.address} to another route
        </DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Move to</Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className={classes.autocompleteInput}
                freeSolo
                disableClearable
                options={routesField.input.value.filter(
                  (option: any) => option.routeName !== routeName,
                )}
                getOptionLabel={(option: any) => option.routeName}
                renderOption={(option) => {
                  return (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span>{option.routeName} - </span>
                      <div
                        style={{
                          backgroundColor: option.routeColor?.colorTwo,
                          width: "20px",
                          height: "20px",
                          borderRadius: "5px",
                          marginLeft: "5px",
                        }}
                      />
                    </div>
                  );
                }}
                onChange={(_event, value: any) => {
                  setTargetRoute(
                    find(routesField.input.value, [
                      "routeName",
                      value.routeName,
                    ]),
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Existing Routes"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, type: "search" }}
                  />
                )}
              />
            </Grid>
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <Button
            onClick={handleMoveRouteLocation}
            color="primary"
            label="Save"
            startIcon={
              isLoading && (
                <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
              )
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isLoading}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <AiLoading isLoading={isLoading} />
      </Dialog>
    </>
  );
};

export default MoveRouteLocationFromMarkerButton;
